import React, { useState } from "react";
import { StripeService } from "../../services/stripeService";
import { ActiveSubscription } from "../../services/stripeTypes";
import Loader from "../../components/Loader";
import { getFunctions, httpsCallable } from "firebase/functions";

interface Props {
  uid: string | null;
  subscription: ActiveSubscription | null;
  onSubscriptionChange?: () => void;
}

// Feedback Options Config
const REASONS_Q1 = [
  { id: "no_time", title: "I did not have enough time to explore it properly", subtitle: "Life got busy, the trial slipped by" },
  { id: "missing_features", title: "It was missing features I need", subtitle: "Something I expected was not there" },
  { id: "too_complex", title: "It felt too complex or hard to navigate", subtitle: "Wasn't sure where to start" },
  { id: "not_right_fit", title: "It is not quite right for my situation", subtitle: "Not the right fit for how I train or work" },
  { id: "cost", title: "The cost isn't worth it at this stage", subtitle: "I would need to see more value first" },
  { id: "using_else", title: "I am already using something else", subtitle: "Going with a different platform" },
];

const REASONS_Q2 = [
  { id: "onboarding", title: "A clearer getting-started experience", subtitle: "More guidance in the first few days" },
  { id: "metrics", title: "More metrics or data visualisation", subtitle: "Deeper insight into training load or readiness" },
  { id: "team_features", title: "Better coach or team features", subtitle: "Managing multiple athletes in one place" },
  { id: "wearables", title: "Wearable or GPS sync", subtitle: "Didn't give me what I needed" },
  { id: "pricing", title: "A lower price point or flexible plan", subtitle: "Something that fits my budget" },
  { id: "just_not_for_me", title: "Nothing, it is just not for me right now", subtitle: "" },
];

const StripeSubscriptionActions: React.FC<Props> = ({
  uid,
  subscription,
  onSubscriptionChange,
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  
  // New Feedback State
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [pendingCancelType, setPendingCancelType] = useState<"immediate" | "periodEnd" | null>(null);
  const [feedbackStep, setFeedbackStep] = useState(1);
  const [feedback, setFeedback] = useState({
    q1: null as string | null,
    q2: [] as string[],
    q3: "",
  });

  const stripeDetails = subscription?.stripeDetails;
  const subscriptionId = stripeDetails?.subscriptionId ?? null;
  const subscriptionStatus = stripeDetails?.subscriptionStatus ?? null;

  const isActive = subscriptionStatus === "active";
  const isTrial = stripeDetails?.subscriptionStatus === "trialing";
  const isIncomplete = subscriptionStatus === "incomplete" || subscriptionStatus === "past_due";
  const isCanceled = stripeDetails?.cancelAt !== 0 && stripeDetails?.cancelAt !== null;
  const canCancel = isActive || isIncomplete || isTrial;

  /** Intercept cancellation to show feedback first */
  const promptFeedback = (type: "immediate" | "periodEnd") => {
    setPendingCancelType(type);
    setFeedbackStep(1);
    setFeedback({ q1: null, q2: [], q3: "" }); // Reset feedback
    setCancelDialogOpen(false);
    setFeedbackModalOpen(true);
  };

  /** Final execution after feedback is collected */
    const executeCancelWithFeedback = async () => {
    if (!pendingCancelType) return;
    setFeedbackModalOpen(false);
    
    // Construct the payload with full context
    const feedbackPayload = {
      q1: feedback.q1,
      q2: feedback.q2,
      q3: feedback.q3,
      subscriptionType: subscription?.subscriptionType || "unknown",
      cancelType: pendingCancelType // "immediate" or "periodEnd"
    };

    // Pass it into your existing handleCancel
    await handleCancel(pendingCancelType, feedbackPayload);
  };

  /** Handle cancellation (Updated to accept feedback) */
  const handleCancel = async (type: "immediate" | "periodEnd", feedbackData?: { q1: string | null, q2: string[], q3: string }) => {
    if (!subscriptionId || !uid) return;
    setLoading(type);

    try {
      if (type === "immediate") {
        // Assuming you will update StripeService to accept a second parameter
        await StripeService.cancelSubscription(subscriptionId, uid, feedbackData);
      } else {
        await StripeService.cancelAtPeriodEnd(subscriptionId, uid, feedbackData);
      }

      const functions = getFunctions();
      const triggerCheckUserSubscription = httpsCallable(
        functions,
        "triggerCheckUserSubscription"
      );

      await triggerCheckUserSubscription({ userId: uid });

      if (onSubscriptionChange) {
        await onSubscriptionChange();
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to cancel subscription.");
      setLoading(null);
    }
  };

  /** Reactivate (Unchanged) */
  const handleReactivate = async () => {
    if (!subscriptionId || !uid) return;
    setLoading("reactivate");

    try {
      const ok = await StripeService.resumeSubscription(subscriptionId, uid);

      if (ok) {
        const functions = getFunctions();
        const triggerCheckUserSubscription = httpsCallable(
          functions,
          "triggerCheckUserSubscription"
        );

        await triggerCheckUserSubscription({ userId: uid });

        if (onSubscriptionChange) {
          await onSubscriptionChange();
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.reload();
      } else {
        alert("Failed to reactivate subscription.");
        setLoading(null);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to reactivate subscription.");
      setLoading(null);
    }
  };

  // Feedback UI Helpers
  const handleQ1Select = (id: string) => setFeedback(prev => ({ ...prev, q1: id }));
  const handleQ2Toggle = (id: string) => {
    setFeedback(prev => ({
      ...prev,
      q2: prev.q2.includes(id) ? prev.q2.filter(i => i !== id) : [...prev.q2, id]
    }));
  };
  const isStripeSubscriber = subscription?.subscriptionVia === "stripe";
  // Legacy regional prices are closed. A canceled legacy plan cannot be
  // resumed; the new Peak account can still be reactivated.
  const canReactivate = isCanceled && subscription?.stripeDetails?.stripeAccount === "peak";
  return (
    <>
      {loading && <Loader message="Processing, please wait..." />}

      <div>
        <div className="flex justify-between items-center text-sm">
          <div className="mt-4 space-y-2">
            {isStripeSubscriber && (
              <button
                onClick={async () => {
                  setLoading("portal");
                  try {
                    const url = await StripeService.createPortalSession(
                      uid!,
                      `${window.location.origin}/settings/subscription`
                    );
                    if (url) window.location.href = url;
                    else alert("Could not open billing portal.");
                  } catch {
                    alert("Something went wrong.");
                  } finally {
                    setLoading(null);
                  }
                }}
                disabled={!!loading}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold font-grotesk bg-base-200 text-base-content hover:bg-base-300 disabled:opacity-50 transition-colors"
              >
                {loading === "portal" ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                )}
                {loading === "portal" ? "Opening..." : "Manage Billing & Invoices"}
              </button>
            )}

            {canCancel && !isCanceled && (
              <button
                onClick={() => setCancelDialogOpen(true)}
                disabled={!!loading}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold font-grotesk bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/30 disabled:opacity-50 transition-colors"
              >
                {loading && loading !== "portal" ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {loading && loading !== "portal" ? "Processing..." : "Cancel Subscription"}
              </button>
            )}

            {canReactivate && (
              <button
                onClick={handleReactivate}
                disabled={loading === "reactivate"}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold font-grotesk bg-green-500/10 text-green-600 hover:bg-green-500/20 border border-green-500/30 disabled:opacity-50 transition-colors"
              >
                {loading === "reactivate" ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                {loading === "reactivate" ? "Processing..." : "Reactivate Subscription"}
              </button>
            )}
          </div>
          
          <div className="flex space-x-2 items-center">
            <p>
              <span className="font-bold text-md font-grotesk capitalize">
                {subscription?.subscriptionType || "N/A"}
              </span>{" "}
              status:
            </p>
            <span className={`px-2 py-1 rounded-md font-medium text-xs text-base-content ${
              stripeDetails?.cancelAt ? "bg-red-200" : isActive ? "bg-green-200" : isIncomplete ? "bg-orange-200" : isCanceled ? "bg-red-200" : "bg-base-300"
            }`}>
              {stripeDetails?.cancelAt ? "Canceled" : isActive ? "Active" : subscriptionStatus === "incomplete" ? "Payment Required" : subscriptionStatus === "past_due" ? "Past Due" : subscriptionStatus === "trialing" ? "Trial" : isCanceled ? "Canceled" : subscriptionStatus || "Unknown"}
            </span>
          </div>
        </div>

        {isIncomplete && (
          <div className="mt-2 rounded-md bg-orange-500/10 border border-orange-500/30 text-orange-700 dark:text-orange-400 px-3 py-2 text-sm">
            ⚠️ <strong>Payment Required:</strong> Your subscription payment didn't complete. Please update your payment method via "Manage Subscription" or cancel to try again.
          </div>
        )}
               
        {stripeDetails?.subscriptionStatus === 'trialing' && (
          <div className="mt-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-700 dark:text-yellow-400 px-3 py-2 text-sm">
            ⏳ You are currently on a trial. At the end of the trial period, your subscription will be automatically charged.
          </div>
        )}

        {stripeDetails?.cancelAt && stripeDetails.cancelAt !== 0 && (
          <div className="mt-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 px-3 py-2 text-sm">
            ⚠️ Your subscription is scheduled to end at the end of the current billing period.
          </div>
        )}

        {/* ─── Step 1: Legacy Cancellation Choice ─── */}
        {cancelDialogOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-base-100 rounded-2xl shadow-xl w-full max-w-md p-8">
              <h2 className="text-xl font-grotesk font-semibold mb-2">Cancel Subscription</h2>
              <p className="text-base-content/70 text-sm mb-6">Choose how you'd like to cancel your subscription:</p>

              <div className="space-y-3 mb-6">
                {isActive && (
                  <button
                    onClick={() => promptFeedback("periodEnd")}
                    disabled={!!loading}
                    className="w-full text-left p-4 border-2 border-base-300 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base-content mb-1">Cancel at Period End</h3>
                        <p className="text-sm text-base-content/70">Keep access until your current billing period ends, then cancel automatically.</p>
                      </div>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => promptFeedback("immediate")}
                  disabled={!!loading}
                  className="w-full text-left p-4 border-2 border-base-300 rounded-lg hover:border-red-500 hover:bg-red-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-base-content mb-1">Cancel Immediately</h3>
                      <p className="text-sm text-base-content/70">End your subscription right now and lose access immediately.</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="">
                <button
                  onClick={() => setCancelDialogOpen(false)}
                  disabled={!!loading}
                  className="w-full px-4 py-2 rounded-md border border-base-300 hover:bg-base-200 disabled:opacity-50 transition-colors bg-base-300 font-grotesk"
                >
                  Nevermind, go Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Step 2: 3-Step Feedback Modal ─── */}
        {feedbackModalOpen && (
          <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-base-100 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 border border-base-300">
              
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-xl font-grotesk font-semibold mb-1">Before you go, we have three quick questions.</h2>
                <p className="text-sm text-base-content/60">This takes under 60 seconds and helps us build a better product. Your feedback is read by the team, not a bot.</p>
              </div>

              {/* Step 1 */}
              {feedbackStep === 1 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-base-content/40 mb-1">QUESTION 1 OF 3</p>
                  <h3 className="text-lg font-semibold mb-4">What is the main reason you are cancelling?</h3>
                  <p className="text-sm text-base-content/60 mb-4">Select the one that fits best.</p>
                  
                  <div className="space-y-2">
                    {REASONS_Q1.map(reason => (
                      <button
                        key={reason.id}
                        onClick={() => handleQ1Select(reason.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          feedback.q1 === reason.id 
                            ? 'border-lime bg-lime/10' 
                            : 'border-base-300 hover:border-base-content/30'
                        }`}
                      >
                        <p className="font-medium text-sm text-base-content">{reason.title}</p>
                        {reason.subtitle && <p className="text-xs text-base-content/50 mt-0.5">{reason.subtitle}</p>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {feedbackStep === 2 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-base-content/40 mb-1">QUESTION 2 OF 3</p>
                  <h3 className="text-lg font-semibold mb-4">What would have made you stay?</h3>
                  <p className="text-sm text-base-content/60 mb-4">Select all that apply.</p>
                  
                  <div className="space-y-2">
                    {REASONS_Q2.map(reason => (
                      <button
                        key={reason.id}
                        onClick={() => handleQ2Toggle(reason.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          feedback.q2.includes(reason.id) 
                            ? 'border-lime bg-lime/10' 
                            : 'border-base-300 hover:border-base-content/30'
                        }`}
                      >
                        <p className="font-medium text-sm text-base-content">{reason.title}</p>
                        {reason.subtitle && <p className="text-xs text-base-content/50 mt-0.5">{reason.subtitle}</p>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {feedbackStep === 3 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-base-content/40 mb-1">QUESTION 3 OF 3 (OPTIONAL)</p>
                  <h3 className="text-lg font-semibold mb-4">Anything else you would like us to know?</h3>
                  <p className="text-sm text-base-content/60 mb-4">Your words go directly to the team who built this. Genuinely appreciate it.</p>
                  
                  <textarea
                    value={feedback.q3}
                    onChange={(e) => setFeedback(prev => ({ ...prev, q3: e.target.value }))}
                    rows={4}
                    placeholder="Share your thoughts..."
                    className="w-full p-3 bg-base-200 border border-base-300 rounded-lg text-base-content text-sm focus:outline-none focus:border-lime resize-none"
                  />
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-base-300">
                <button
                  onClick={() => {
                    if (feedbackStep === 1) {
                      setFeedbackModalOpen(false);
                    } else {
                      setFeedbackStep(prev => prev - 1);
                    }
                  }}
                  className="text-sm text-base-content/60 hover:text-base-content font-medium"
                >
                  {feedbackStep === 1 ? "Nevermind, go back" : "Back"}
                </button>

                <div className="flex gap-3">
                  {feedbackStep < 3 ? (
                    <button
                      onClick={() => setFeedbackStep(prev => prev + 1)}
                      className="px-5 py-2 bg-lime text-black rounded-full font-semibold text-sm hover:bg-lime/80 transition-colors"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={executeCancelWithFeedback}
                      className="px-5 py-2 bg-red-600 text-white rounded-full font-semibold text-sm hover:bg-red-700 transition-colors"
                    >
                      Proceed with Cancellation
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StripeSubscriptionActions;